import React, {
  useMemo,
  useState,
  DetailedHTMLProps,
  HTMLAttributes,
  memo,
  useRef,
} from "react";
import ReactMarkdown from "react-markdown";
import html2canvas from "html2canvas";
import { Message } from "../types";
import LoadingDots from "./LoadingDots";
import rehypeKatex from "rehype-katex";
import rehypeHighlight from "rehype-highlight";
import remarkMath from "remark-math";
import remarkGfm from "remark-gfm";
import remarkBreaks from "remark-breaks";
import { ThumbsUp, ThumbsDown, Copy, Check, Camera, GitBranch, Pen } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../components/ui/Tooltip";

interface ChatMessageProps {
  message: Message;
  isLatest: boolean;
  isStreaming?: boolean;
  onBranch?: (messageId: string) => void;
  onRetry?: () => void;
  onEdit?: (messageId: string, newContent: string) => void;
}

const ChatMessage: React.FC<ChatMessageProps> = ({
  message,
  isLatest,
  isStreaming = false,
  onBranch,
  onRetry,
  onEdit,
}) => {
  const isUser = message.role === "user";
  const [liked, setLiked] = useState(false);
  const [disliked, setDisliked] = useState(false);
  const [showCopySuccess, setShowCopySuccess] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState(message.content);
  const messageRef = useRef<HTMLDivElement>(null);

  const content = useMemo(() => message.content, [message.content]);

  // Update editContent if message content changes externally (though usually it won't while editing)
  React.useEffect(() => {
    setEditContent(message.content);
  }, [message.content]);

  const handleLike = () => {
    setLiked(!liked);
    if (disliked) setDisliked(false);
  };

  const handleDislike = () => {
    setDisliked(!disliked);
    if (liked) setLiked(false);
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(content);
      setShowCopySuccess(true);
      setTimeout(() => setShowCopySuccess(false), 2000);
    } catch (err) {
      console.error("Failed to copy text:", err);
    }
  };

  const handleSnapshot = async () => {
    if (messageRef.current) {
      try {
        const canvas = await html2canvas(messageRef.current, {
           scale: 2, // higher resolution
           useCORS: true,
           backgroundColor: null, // Transparent background if possible, or matches container
           onclone: (clonedDoc : any) => {
             // Find the cloned element
             const clonedElement = clonedDoc.body.querySelector(`[data-message-id="${message.id}"]`);
             if (clonedElement) {
               // Show watermark
               const watermark = clonedElement.querySelector('.watermark');
               if (watermark) {
                 (watermark as HTMLElement).style.display = 'flex';
               }
               // Hide actions container
               const actions = clonedElement.querySelector('.message-actions');
               if (actions) {
                 (actions as HTMLElement).style.display = 'none';
               }
             }
           }
        } as any);
        const dataUrl = canvas.toDataURL("image/png");
        const link = document.createElement("a");
        link.href = dataUrl;
        link.download = `chat-snapshot-${message.id}.png`;
        link.click();
      } catch (err) {
        console.error("Snapshot failed:", err);
      }
    }
  };

  const handleSaveEdit = () => {
    if (onEdit && editContent.trim() !== content) {
      onEdit(message.id, editContent);
    }
    setIsEditing(false);
  };

  const handleCancelEdit = () => {
    setEditContent(content);
    setIsEditing(false);
  };

  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"} mb-4`}>
      <div className="max-w-[80%]">
        <div
          ref={messageRef}
          data-message-id={message.id}
          className={`relative rounded-lg p-4 group ${
            isUser
              ? "bg-green-600 text-white rounded-br-none"
              : "bg-gray-100 text-gray-800 rounded-bl-none"
          }`}
        >
          {/* Watermark overlay (hidden by default, shown in snapshot) */}
          <div className="watermark hidden absolute inset-0 items-center justify-center opacity-10 pointer-events-none mb-10">
             <div className="flex flex-col items-center">
                 <img src="/bg-1.jpeg" alt="Logo" className="w-20 h-20 opacity-20 grayscale" />
                 <span className="text-xl font-bold mt-2 uppercase tracking-widest opacity-30">Ask The Bill</span>
             </div>
          </div>
          
          <div className="markdown prose w-full md:max-w-full break-words relative z-10">
            {isEditing ? (
              <div className="w-full">
                <textarea
                  value={editContent}
                  onChange={(e) => setEditContent(e.target.value)}
                  className="w-full p-2 text-gray-800 bg-white rounded-md focus:outline-none focus:ring-2 focus:ring-green-300 min-h-[100px]"
                />
                <div className="flex justify-end gap-2 mt-2">
                   <button onClick={handleCancelEdit} className="px-3 py-1 text-sm bg-gray-200 text-gray-700 rounded hover:bg-gray-300">Cancel</button>
                   <button onClick={handleSaveEdit} className="px-3 py-1 text-sm bg-white text-green-700 font-medium rounded hover:bg-green-50">Save</button>
                </div>
              </div>
            ) : (
            <ReactMarkdown
              remarkPlugins={[
                remarkGfm,
                remarkBreaks,
                [remarkMath, { inlineMath: true }],
              ]}
              rehypePlugins={[
                rehypeKatex,
                [
                  rehypeHighlight,
                  {
                    detect: true,
                    ignoreMissing: true,
                  },
                ],
              ]}
              components={{
                p: memo(
                  (
                    props?: Omit<
                      DetailedHTMLProps<
                        HTMLAttributes<HTMLParagraphElement>,
                        HTMLParagraphElement
                      >,
                      "ref"
                    >
                  ) => {
                    return (
                      <p className="whitespace-pre-wrap">{props?.children}</p>
                    );
                  }
                ),
              }}
            >
              {content}
            </ReactMarkdown>
          )}
          </div>


          {!isStreaming && !isEditing && (
            <div className={`message-actions flex items-center gap-2 mt-2 ${isUser ? "justify-end text-green-100" : "text-gray-500"}`}>
               {/* Copy Button (Available for both) */}
                <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      onClick={handleCopy}
                      className={`p-1 rounded hover:bg-white/10 transition-colors ${
                        isUser ? "hover:bg-green-700 text-green-100" : "hover:bg-gray-200 text-gray-500"
                      }`}
                      aria-label="Copy message"
                    >
                      {showCopySuccess ? (
                        <Check size={14} />
                      ) : (
                        <Copy size={14} />
                      )}
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{showCopySuccess ? "Copied!" : "Copy"}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>

              {/* Edit Button (User only) */}
              {isUser && onEdit && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        onClick={() => setIsEditing(true)}
                        className="p-1 rounded hover:bg-green-700 text-green-100 transition-colors"
                        aria-label="Edit message"
                      >
                        <Pen size={14} />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Edit</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}

              {/* Snapshot Button */}
               <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      onClick={handleSnapshot}
                      className={`p-1 rounded transition-colors ${
                        isUser ? "hover:bg-green-700 text-green-100" : "hover:bg-gray-200 text-gray-500"
                      }`}
                      aria-label="Snapshot"
                    >
                      <Camera size={14} />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Snapshot</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              
              {/* Branch Button (Only handling if onBranch is provided) */}
              {onBranch && (
                 <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        onClick={() => onBranch(message.id)}
                        className={`p-1 rounded transition-colors ${
                           isUser ? "hover:bg-green-700 text-green-100" : "hover:bg-gray-200 text-gray-500"
                        }`}
                        aria-label="Branch chat"
                      >
                        <GitBranch size={14} />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Branch Chat</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}

              {!isUser && (
                <>
                  <div className="w-px h-3 bg-gray-300/50 mx-1" /> {/* Divider */}
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          onClick={handleLike}
                          className={`p-1 rounded hover:bg-gray-200 transition-colors ${
                            liked ? "text-green-600" : "text-gray-500"
                          }`}
                          aria-label="Like message"
                        >
                          <ThumbsUp size={14} />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Like</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>

                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          onClick={handleDislike}
                          className={`p-1 rounded hover:bg-gray-200 transition-colors ${
                            disliked ? "text-red-500" : "text-gray-500"
                          }`}
                          aria-label="Dislike message"
                        >
                          <ThumbsDown size={14} />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Dislike</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </>
              )}
            </div>
          )}
        </div>
        {!isUser && isStreaming && (
          <div className="mt-2 text-gray-500">
            <LoadingDots />
          </div>
        )}
        {message.status === 'error' && (
           <div className="mt-2 text-red-500 text-sm flex items-center gap-2">
             <span>Failed to send.</span>
             {onRetry && <button onClick={() => onRetry()} className="underline hover:text-red-700">Retry</button>}
           </div>
        )}
      </div>
    </div>
  );
};


export default ChatMessage;
